import React, { useState, useEffect } from "react";
import {
  ArrowRight,
  Check,
  ChevronDown,
  TrendingUp,
  ShieldCheck,
  BarChart3,
  FileText,
  Landmark,
  Layers,
  Infinity,
} from "lucide-react";
import "./AC24100.scss";

// ── API ────────────────────────────────────────────────────────
const BASE_URL    = "https://global.cilpron.com/api";
const PRODUCT_ID  = "1D4DF843-76E7-4F82-97A5-0D13AC821E9D"; // Accounting

// ── Types ──────────────────────────────────────────────────────
interface ApiPlan {
  id:              string;
  productId:       string;
  name:            string;
  description:     string | null;
  billingInterval: string;
  trialDays:       number;
  sortOrder:       number;
  isActive:        boolean;
  isPublic:        boolean;
  features: { $values: { featureId: string; displayLabel: string | null; featureValue: string; valueType: string }[] };
}

interface ApiPrice {
  id:            string;
  plan_id:       string;
  currency:      string;
  amount:        number;       // cents
  pricing_model: string;
  unit_amount:   number | null;
  unit_label:    string | null;
  free_units:    number;
  is_active:     boolean;
}

type Cycle = "monthly" | "annual" | "lifetime";

// ── Static content ─────────────────────────────────────────────
const FEATURES = [
  {
    icon: Layers,
    photo: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=800&q=80&auto=format&fit=crop",
    title: "Close the books once, not per entity",
    body: "Consolidate across subsidiaries and currencies automatically. Intercompany eliminations and FX translation run in the background, not as a quarter-end fire drill.",
  },
  {
    icon: FileText,
    photo: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&q=80&auto=format&fit=crop",
    title: "An audit trail on every transaction",
    body: "Every journal entry, approval, and edit is timestamped and attributed. When auditors ask for evidence, you export it instead of reconstructing it.",
  },
  {
    icon: BarChart3,
    photo: "https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=800&q=80&auto=format&fit=crop",
    title: "Statements your CFO can present same-day",
    body: "P&L, balance sheet, and cash flow refresh in real time as transactions post, so board decks run on current numbers instead of last week's export.",
  },
];

const STATS = [
  { value: "6.2 days",  label: "average reduction in monthly close time" },
  { value: "99.95%",   label: "platform uptime, backed by SLA" },
  { value: "$2.1B+",   label: "in transactions processed monthly" },
  { value: "3,400+",   label: "finance teams closing their books on the platform" },
];

const STORIES = [
  {
    company: "Harrow & Vance", industry: "Professional Services",
    metric: "Close time cut from 9 to 3 days",
    quote: "Consolidating four entities used to mean a week of spreadsheet reconciliation. Now it's a checklist our controller finishes before lunch.",
    person: "Controller",
  },
  {
    company: "Greenfield Logistics", industry: "Transportation",
    metric: "Zero audit findings, two years running",
    quote: "The audit trail is the reason our external auditors stopped sampling and started trusting the system. That alone cut our audit fee by a third.",
    person: "VP of Finance",
  },
  {
    company: "Solene Beauty Group", industry: "Consumer Retail",
    metric: "Consolidated 6 entities in one close",
    quote: "We run six legal entities across three currencies. This is the first platform that's actually made consolidation boring, in the best way.",
    person: "CFO",
  },
];

const FAQS = [
  {
    q: "Can we migrate historical data from QuickBooks or NetSuite?",
    a: "Yes. Guided migration tools import chart of accounts, transaction history, and open balances from QuickBooks, NetSuite, and Xero, with full reconciliation before go-live.",
  },
  {
    q: "Does the platform support multi-entity consolidation?",
    a: "Pro and Lifetime plans include automated intercompany eliminations, FX translation, and consolidated reporting across unlimited entities.",
  },
  {
    q: "How does the audit trail work?",
    a: "Every transaction, approval, and edit is logged with user, timestamp, and prior value. Audit trails are exportable and immutable — they can't be edited or deleted, even by admins.",
  },
  {
    q: "Is the platform SOC 2 and SOX compliant?",
    a: "The platform is SOC 2 Type II certified. Pro and Lifetime plans include SOX-ready controls, segregation of duties enforcement, and dedicated compliance reporting.",
  },
];

// ── Pricing tiers to display (3 cards per cycle view) ─────────
// plan_id pairs resolved from the API
const TIER_CONFIG = [
  {
    tier:       "solo",
    label:      "Solo",
    blurb:      "For freelancers and sole traders managing their own books.",
    monthlyId:  "a6afc45e-3250-49de-b394-d1a1085e8637",
    yearlyId:   "8041f0a8-9703-4ad9-bea9-03c58b3f02e4",
    lifetimeId: null,
    cta:        "Start free trial",
    highlighted: false,
    features: [
      "Unlimited invoices",
      "1 user",
      "Bank reconciliation",
      "Basic tax reports",
      "14-day free trial",
    ],
  },
  {
    tier:       "business",
    label:      "Business",
    blurb:      "For growing teams running payroll and multi-currency.",
    monthlyId:  "bc5ba4ab-b352-4b46-87ff-85b06d5f324a",
    yearlyId:   "530694c6-2a18-4f97-a4f3-bc1425f2046f",
    lifetimeId: null,
    cta:        "Start free trial",
    highlighted: true,
    features: [
      "5 users",
      "Payroll up to 20 employees",
      "Multi-currency",
      "P&L & balance sheet reports",
      "14-day free trial",
    ],
  },
  {
    tier:       "pro",
    label:      "Pro",
    blurb:      "For firms needing unlimited entities and full audit control.",
    monthlyId:  "c3c2f9b0-7811-4f78-b537-6068a08c0d61",
    yearlyId:   "cc488081-7e62-4ec8-b5c5-e7a646dd7cb9",
    lifetimeId: null,
    cta:        "Start free trial",
    highlighted: false,
    features: [
      "Unlimited users & entities",
      "Accountant access",
      "Full audit trail",
      "API access",
      "14-day free trial",
    ],
  },
];

const LIFETIME_CONFIG = {
  tier:      "lifetime",
  label:     "Lifetime",
  blurb:     "Business-level accounting forever. Pay once, own it.",
  planId:    "9986aa43-e80c-454a-81f7-f601b17fc052",
  cta:       "Buy once, use forever",
  features: [
    "Everything in Business",
    "Unlimited invoices & users",
    "No recurring fees — ever",
    "All future updates included",
    "Priority support",
  ],
};

const FREE_PLAN_ID = "9d78ac1f-2c1c-4996-95d3-1e206481ddc3";

// ── Format price from cents ────────────────────────────────────
function dollars(cents: number): string {
  const d = cents / 100;
  return `$${d % 1 === 0 ? d.toFixed(0) : d.toFixed(2)}`;
}

// ── Component ──────────────────────────────────────────────────
const AC24100: React.FC = () => {
  const [cycle, setCycle]     = useState<Cycle>("monthly");
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [priceMap, setPriceMap] = useState<Record<string, ApiPrice>>({});
  const [loading, setLoading]   = useState(true);
  const [fetchError, setFetchError] = useState(false);

  // ── Fetch plans + prices — no auth required ────────────────
  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetch(`${BASE_URL}/Plans/by-product/${PRODUCT_ID}`, { headers: { accept: "*/*" } }).then(r => r.json()),
      fetch(`${BASE_URL}/Prices`, { headers: { accept: "*/*" } }).then(r => r.json()),
    ])
      .then(([_planJson, priceJson]) => {
        // Build plan_id → Price map (prefer USD)
        const allPrices: ApiPrice[] = priceJson?.data?.$values ?? [];
        const map: Record<string, ApiPrice> = {};
        allPrices.forEach(p => {
          if (!map[p.plan_id] || p.currency === "USD") map[p.plan_id] = p;
        });
        setPriceMap(map);
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false));
  }, []);

  // ── Get display price for a plan ID ───────────────────────
  const getPrice = (planId: string | null): ApiPrice | null =>
    planId ? (priceMap[planId] ?? null) : null;

  const displayPrice = (price: ApiPrice | null, interval: "monthly" | "yearly" | "lifetime"): {
    main: string; sub: string; note?: string;
  } => {
    if (!price) return { main: "—", sub: "" };
    if (price.amount === 0) return { main: "Free", sub: "forever" };

    if (interval === "lifetime") {
      return { main: dollars(price.amount), sub: "one-time payment" };
    }
    if (interval === "yearly") {
      const perMonth = price.amount / 12 / 100;
      const perMonthStr = `$${perMonth % 1 === 0 ? perMonth.toFixed(0) : perMonth.toFixed(2)}`;
      return {
        main: perMonthStr,
        sub:  "/mo",
        note: `Billed ${dollars(price.amount)}/year`,
      };
    }
    return { main: dollars(price.amount), sub: "/mo" };
  };

  // ── Resolve which plan ID to use for a tier ────────────────
  const planIdForCycle = (cfg: typeof TIER_CONFIG[0]): string | null => {
    if (cycle === "annual")   return cfg.yearlyId;
    return cfg.monthlyId;
  };

  const freePrice = getPrice(FREE_PLAN_ID);
  const lifetimePrice = getPrice(LIFETIME_CONFIG.planId);

  return (
    <div className="wrap">

      {/* ── HERO ─────────────────────────────────────────────── */}
      <section className="hero">
        <div className="hero-bg" aria-hidden="true" />
        <div className="hero-inner">
          <div className="hero-copy">
            <span className="eyebrow">Accounting Platform</span>
            <h1>Close your books in days, not weeks</h1>
            <p className="hero-sub">
              One ledger for invoicing, reconciliation, and multi-entity
              consolidation — built for finance teams who are tired of
              reconstructing the close from spreadsheets every single month.
            </p>
            <div className="hero-cta">
              <button className="btn-primary">Start free trial <ArrowRight size={16} /></button>
              <button className="btn-ghost">Book a demo</button>
            </div>
            <p className="hero-fineprint">
              No credit card required &nbsp;·&nbsp; Free for 14 days &nbsp;·&nbsp; Cancel anytime
            </p>
          </div>
          <div className="hero-art">
            <img
              className="hero-photo"
              src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1200&q=80&auto=format&fit=crop"
              alt="Finance team reviewing financial statements"
            />
            <div className="hero-photo-card">
              <span className="hero-photo-card-label">Close progress</span>
              <span className="hero-photo-card-value">92%</span>
              <span className="hero-photo-card-sub">Day 3 of 5 · on track</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── LOGO BAR ─────────────────────────────────────────── */}
      <section className="logobar">
        <span className="logobar-label">Trusted by finance teams at</span>
        <div className="logobar-strip">
          {[
            { name: "Harrow & Vance", mark: "HV" },
            { name: "Greenfield", mark: "GF" },
            { name: "Solene Beauty", mark: "SB" },
            { name: "Aldermoor Capital", mark: "AC" },
            { name: "Pelican Health", mark: "PH" },
          ].map((c) => (
            <div className="logobar-item" key={c.name}>
              <span className="logobar-mark">{c.mark}</span>
              <span className="logobar-name">{c.name}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── FEATURES ─────────────────────────────────────────── */}
      <section className="section">
        <div className="section-head">
          <span className="eyebrow">Platform</span>
          <h2>Everything a finance team needs to close with confidence</h2>
        </div>
        <div className="feature-grid">
          {FEATURES.map((f) => (
            <div className="feature-card" key={f.title}>
              <div className="feature-photo-wrap">
                <img className="feature-photo" src={f.photo} alt="" />
                <span className="feature-icon"><f.icon size={18} strokeWidth={1.75} /></span>
              </div>
              <h3>{f.title}</h3>
              <p>{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── STATS ────────────────────────────────────────────── */}
      <section className="stats">
        {STATS.map((s) => (
          <div className="stat" key={s.label}>
            <div className="stat-value">{s.value}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </section>

      {/* ── PRICING ──────────────────────────────────────────── */}
      <section className="section" id="pricing">
        <div className="section-head">
          <span className="eyebrow">Pricing</span>
          <h2>Plans built around how your books grow</h2>
          <p className="section-sub">
            All paid plans include a 14-day free trial. No credit card required to start.
          </p>

          {/* Cycle toggle */}
          <div className="toggle">
            <button className={cycle === "monthly"  ? "toggle-btn active" : "toggle-btn"} onClick={() => setCycle("monthly")}>
              Monthly
            </button>
            <button className={cycle === "annual"   ? "toggle-btn active" : "toggle-btn"} onClick={() => setCycle("annual")}>
              Annual <span className="toggle-save">Save 20%</span>
            </button>
            <button className={cycle === "lifetime" ? "toggle-btn active" : "toggle-btn"} onClick={() => setCycle("lifetime")}>
              Lifetime <span className="toggle-lifetime"><Infinity size={11} /></span>
            </button>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="pricing-loading">
            <div className="pricing-spinner" />
            <span>Loading plans…</span>
          </div>
        )}

        {/* Fetch error */}
        {fetchError && !loading && (
          <div className="pricing-error">
            Unable to load live pricing. Please refresh the page or contact support.
          </div>
        )}

        {/* Free plan banner */}
        {!loading && !fetchError && (
          <div className="free-banner">
            <div className="free-banner-left">
              <span className="free-badge">Free forever</span>
              <div>
                <div className="free-banner-title">Get started at no cost</div>
                <div className="free-banner-sub">5 invoices/month · 1 user · basic income &amp; expense tracking</div>
              </div>
            </div>
            <button className="btn-secondary free-banner-cta">Create free account →</button>
          </div>
        )}

        {/* Lifetime view — single hero card */}
        {!loading && !fetchError && cycle === "lifetime" && (
          <div className="lifetime-card">
            <div className="lifetime-icon"><Infinity size={32} strokeWidth={1.5} /></div>
            <div className="lifetime-label">Lifetime Plan</div>
            <h3 className="lifetime-name">{LIFETIME_CONFIG.label}</h3>
            <p className="lifetime-blurb">{LIFETIME_CONFIG.blurb}</p>
            <div className="lifetime-price">
              {loading ? (
                <span className="price-loading">—</span>
              ) : (
                <>
                  <span className="lifetime-amount">
                    {lifetimePrice ? dollars(lifetimePrice.amount) : "—"}
                  </span>
                  <span className="lifetime-sub">one-time payment</span>
                </>
              )}
            </div>
            <button className="btn-primary lifetime-cta">{LIFETIME_CONFIG.cta} <ArrowRight size={16} /></button>
            <ul className="tier-features lifetime-features">
              {LIFETIME_CONFIG.features.map(f => (
                <li key={f}><Check size={15} strokeWidth={2.25} />{f}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Monthly / Annual — 3-card grid */}
        {!loading && !fetchError && cycle !== "lifetime" && (
          <div className="pricing-grid">
            {TIER_CONFIG.map((cfg) => {
              const planId = planIdForCycle(cfg);
              const price  = getPrice(planId);
              const { main, sub, note } = displayPrice(price, cycle === "annual" ? "yearly" : "monthly");

              return (
                <div key={cfg.tier} className={cfg.highlighted ? "tier tier-highlight" : "tier"}>
                  {cfg.highlighted && <span className="tier-badge">Most popular</span>}
                  <h3>{cfg.label}</h3>
                  <p className="tier-blurb">{cfg.blurb}</p>

                  <div className="tier-price">
                    <span className="price-amount">{main}</span>
                    <span className="price-period">{sub}</span>
                  </div>
                  {note && <div className="tier-note">{note}</div>}

                  <button className={cfg.highlighted ? "btn-primary full" : "btn-secondary full"}>
                    {cfg.cta}
                  </button>

                  <ul className="tier-features">
                    {cfg.features.map(f => (
                      <li key={f}><Check size={15} strokeWidth={2.25} />{f}</li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ── CLIENT STORIES ───────────────────────────────────── */}
      <section className="section">
        <div className="section-head">
          <span className="eyebrow">Customer stories</span>
          <h2>Results from finance teams running on the platform</h2>
        </div>
        <div className="story-grid">
          {STORIES.map((s) => (
            <div className="story-card" key={s.company}>
              <div className="story-metric"><TrendingUp size={16} strokeWidth={2} />{s.metric}</div>
              <p className="story-quote">&ldquo;{s.quote}&rdquo;</p>
              <div className="story-attrib">
                <div className="story-company">{s.company}</div>
                <div className="story-role">{s.person} &middot; {s.industry}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── SECURITY ─────────────────────────────────────────── */}
      <section className="security">
        <div className="security-item"><ShieldCheck size={18} strokeWidth={1.75} />SOC 2 Type II compliant</div>
        <div className="security-item"><Landmark size={18} strokeWidth={1.75} />Bank-level encryption</div>
        <div className="security-item"><FileText size={18} strokeWidth={1.75} />Immutable audit trail</div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────── */}
      <section className="section faq-section">
        <div className="section-head">
          <span className="eyebrow">Questions</span>
          <h2>Frequently asked questions</h2>
        </div>
        <div className="faq-list">
          {FAQS.map((item, i) => (
            <div className="faq-item" key={item.q}>
              <button className="faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)} aria-expanded={openFaq === i}>
                {item.q}
                <ChevronDown size={18} className={openFaq === i ? "faq-chev open" : "faq-chev"} />
              </button>
              {openFaq === i && <p className="faq-a">{item.a}</p>}
            </div>
          ))}
        </div>
      </section>

      {/* ── FINAL CTA ────────────────────────────────────────── */}
      <section className="cta-band">
        <h2>See your own books on the platform</h2>
        <p>Set up takes about fifteen minutes. No credit card required.</p>
        <div className="hero-cta center">
          <button className="btn-primary">Start free trial <ArrowRight size={16} /></button>
          <button className="btn-ghost on-dark">Book a demo</button>
        </div>
      </section>

      <style>{`
        .wrap {
          --ink: #14171F; --slate: #5B6472; --line: #E2E5EA;
          --surface: #F7F8FA; --primary: #2A5BD7; --primary-dark: #1E45AC;
          --success: #1F8A5F; --gold: #B45309;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          color: var(--ink); max-width: 1180px; margin: 0 auto; padding: 0 24px;
        }
        .wrap * { box-sizing: border-box; }
        .eyebrow { display: inline-block; font-size: 13px; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase; color: var(--primary); margin-bottom: 14px; }
        h1,h2,h3 { font-family:'Inter Tight','Inter',sans-serif; font-weight:700; letter-spacing:-0.01em; margin:0; }
        p { line-height:1.6; color:var(--slate); margin:0; }

        .btn-primary,.btn-secondary,.btn-ghost { font-family:inherit; font-size:15px; font-weight:600; border-radius:8px; padding:12px 22px; cursor:pointer; display:inline-flex; align-items:center; gap:8px; transition:background .15s,border-color .15s; border:1.5px solid transparent; }
        .btn-primary { background:var(--primary); color:#fff; }
        .btn-primary:hover { background:var(--primary-dark); }
        .btn-secondary { background:#fff; color:var(--ink); border-color:var(--line); }
        .btn-secondary:hover { border-color:var(--ink); }
        .btn-ghost { background:transparent; color:var(--ink); border-color:var(--line); }
        .btn-ghost:hover { background:var(--surface); }
        .btn-ghost.on-dark { color:#fff; border-color:rgba(255,255,255,0.35); }
        .btn-ghost.on-dark:hover { background:rgba(255,255,255,0.08); }
        .full { width:100%; justify-content:center; }

        /* HERO */
        .hero { position:relative; left:50%; right:50%; width:100vw; margin-left:-50vw; margin-right:-50vw; padding:76px 0 72px; overflow:hidden; }
        .hero-bg { position:absolute; inset:0; z-index:0; background:radial-gradient(680px 420px at 78% 18%,rgba(42,91,215,.10),transparent 60%),radial-gradient(520px 360px at 8% 85%,rgba(31,138,95,.07),transparent 60%),linear-gradient(180deg,#FBFCFE 0%,#FFFFFF 70%); }
        .hero-bg::after { content:""; position:absolute; inset:0; background-image:linear-gradient(to right,rgba(20,23,31,.035) 1px,transparent 1px),linear-gradient(to bottom,rgba(20,23,31,.035) 1px,transparent 1px); background-size:64px 64px; mask-image:radial-gradient(800px 480px at 70% 10%,black,transparent 75%); }
        .hero-inner { position:relative; z-index:1; max-width:1180px; margin:0 auto; padding:0 24px; display:grid; grid-template-columns:1fr 1.08fr; gap:56px; align-items:center; }
        .hero h1 { font-size:46px; line-height:1.13; margin-bottom:18px; }
        .hero-sub { font-size:17px; max-width:480px; margin-bottom:30px; }
        .hero-cta { display:flex; gap:12px; margin-bottom:16px; }
        .hero-cta.center { justify-content:center; }
        .hero-fineprint { font-size:13px; color:var(--slate); }
        .hero-art { position:relative; display:flex; justify-content:center; }
        .hero-photo { width:100%; aspect-ratio:4/3.1; object-fit:cover; border-radius:16px; border:1px solid var(--line); box-shadow:0 28px 64px -20px rgba(20,23,31,.28); }
        .hero-photo-card { position:absolute; left:-28px; bottom:-22px; background:#fff; border:1px solid var(--line); border-radius:12px; box-shadow:0 14px 36px -14px rgba(20,23,31,.28); padding:16px 20px; display:flex; flex-direction:column; gap:2px; }
        .hero-photo-card-label { font-size:11px; font-weight:600; color:var(--slate); text-transform:uppercase; letter-spacing:.03em; }
        .hero-photo-card-value { font-family:'Inter Tight',sans-serif; font-size:26px; font-weight:700; color:var(--ink); }
        .hero-photo-card-sub { font-size:12px; font-weight:600; color:var(--success); }

        /* LOGO BAR */
        .logobar { padding:40px 0 64px; border-top:1px solid var(--line); }
        .logobar-label { display:block; text-align:center; font-size:12px; font-weight:600; letter-spacing:.04em; text-transform:uppercase; color:var(--slate); margin-bottom:28px; }
        .logobar-strip { display:flex; align-items:stretch; justify-content:center; flex-wrap:wrap; border:1px solid var(--line); border-radius:14px; background:var(--surface); overflow:hidden; }
        .logobar-item { display:flex; align-items:center; gap:10px; flex:1 1 0; justify-content:center; padding:22px 20px; border-right:1px solid var(--line); transition:background .15s; }
        .logobar-item:last-child { border-right:none; }
        .logobar-item:hover { background:#fff; }
        .logobar-mark { width:28px; height:28px; border-radius:7px; background:#fff; border:1px solid var(--line); color:#9AA2AE; font-size:11px; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; transition:color .15s,border-color .15s; }
        .logobar-name { font-weight:600; font-size:15px; color:#9AA2AE; white-space:nowrap; transition:color .15s; }
        .logobar-item:hover .logobar-mark { color:var(--primary); border-color:var(--primary); }
        .logobar-item:hover .logobar-name { color:var(--ink); }

        /* SECTION */
        .section { padding:64px 0; }
        .section-head { max-width:640px; margin:0 auto 40px; text-align:center; }
        .section-head h2 { font-size:30px; line-height:1.25; }
        .section-sub { margin-top:10px; font-size:15px; }

        /* FEATURES */
        .feature-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:24px; }
        .feature-card { border:1px solid var(--line); border-radius:12px; padding:0 0 24px; background:#fff; overflow:hidden; }
        .feature-photo-wrap { position:relative; margin-bottom:18px; }
        .feature-photo { width:100%; height:150px; object-fit:cover; display:block; }
        .feature-icon { position:absolute; left:16px; bottom:-16px; width:36px; height:36px; border-radius:9px; background:var(--primary); color:#fff; box-shadow:0 6px 16px -4px rgba(42,91,215,.5); display:flex; align-items:center; justify-content:center; }
        .feature-card h3 { font-size:17px; margin:0 24px 10px; line-height:1.35; }
        .feature-card p { font-size:14.5px; margin:0 24px; }

        /* STATS */
        .stats { display:grid; grid-template-columns:repeat(4,1fr); gap:1px; background:var(--line); border:1px solid var(--line); border-radius:12px; overflow:hidden; }
        .stat { background:var(--surface); padding:30px 20px; text-align:center; }
        .stat-value { font-family:'Inter Tight',sans-serif; font-size:30px; font-weight:700; color:var(--ink); }
        .stat-label { font-size:13px; color:var(--slate); margin-top:6px; line-height:1.4; }

        /* TOGGLE */
        .toggle { display:inline-flex; gap:2px; margin-top:22px; background:var(--surface); border:1px solid var(--line); border-radius:9px; padding:3px; }
        .toggle-btn { font-family:inherit; font-size:14px; font-weight:600; padding:8px 16px; border-radius:7px; border:none; background:none; color:var(--slate); cursor:pointer; display:flex; align-items:center; gap:6px; transition:background .15s,color .15s,box-shadow .15s; }
        .toggle-btn.active { background:#fff; color:var(--ink); box-shadow:0 1px 2px rgba(0,0,0,.08); }
        .toggle-save { font-size:11px; font-weight:700; color:var(--success); }
        .toggle-lifetime { display:inline-flex; align-items:center; color:var(--gold); }

        /* FREE BANNER */
        .free-banner { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:16px; background:var(--surface); border:1px solid var(--line); border-radius:12px; padding:18px 24px; margin-bottom:24px; }
        .free-banner-left { display:flex; align-items:center; gap:14px; }
        .free-badge { font-size:11px; font-weight:700; color:var(--success); background:rgba(31,138,95,.1); padding:4px 10px; border-radius:99px; white-space:nowrap; }
        .free-banner-title { font-size:15px; font-weight:700; color:var(--ink); }
        .free-banner-sub { font-size:13px; color:var(--slate); margin-top:2px; }
        .free-banner-cta { padding:9px 18px; font-size:14px; }

        /* PRICING GRID */
        .pricing-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:20px; margin-top:0; }
        .tier { border:1px solid var(--line); border-radius:14px; padding:28px 24px 24px; background:#fff; position:relative; display:flex; flex-direction:column; }
        .tier-highlight { border-color:var(--primary); box-shadow:0 14px 36px -18px rgba(42,91,215,.35); }
        .tier-badge { position:absolute; top:-12px; left:24px; background:var(--primary); color:#fff; font-size:11.5px; font-weight:700; padding:4px 10px; border-radius:99px; }
        .tier h3 { font-size:19px; }
        .tier-blurb { font-size:13.5px; margin-top:6px; min-height:40px; }
        .tier-price { display:flex; align-items:baseline; gap:6px; margin:18px 0 6px; }
        .price-amount { font-family:'Inter Tight',sans-serif; font-size:36px; font-weight:700; }
        .price-period { font-size:13px; color:var(--slate); }
        .tier-note { font-size:12px; color:var(--slate); margin-bottom:16px; }
        .tier-features { list-style:none; padding:0; margin:22px 0 0; display:flex; flex-direction:column; gap:11px; }
        .tier-features li { display:flex; align-items:flex-start; gap:9px; font-size:14px; color:var(--ink); }
        .tier-features li svg { color:var(--success); flex-shrink:0; margin-top:2px; }

        /* LIFETIME CARD */
        .lifetime-card { max-width:520px; margin:0 auto; border:2px solid var(--gold); border-radius:20px; padding:40px 36px 36px; background:#fff; text-align:center; box-shadow:0 16px 48px -20px rgba(180,83,9,.2); }
        .lifetime-icon { width:56px; height:56px; border-radius:14px; background:rgba(180,83,9,.1); color:var(--gold); display:flex; align-items:center; justify-content:center; margin:0 auto 16px; }
        .lifetime-label { font-size:12px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:var(--gold); margin-bottom:8px; }
        .lifetime-name { font-size:28px; margin-bottom:8px; }
        .lifetime-blurb { font-size:15px; max-width:340px; margin:0 auto 24px; }
        .lifetime-price { margin-bottom:28px; }
        .lifetime-amount { font-family:'Inter Tight',sans-serif; font-size:52px; font-weight:800; color:var(--ink); letter-spacing:-2px; }
        .lifetime-sub { display:block; font-size:14px; color:var(--slate); margin-top:4px; }
        .lifetime-cta { width:100%; justify-content:center; background:var(--gold); border-color:var(--gold); margin-bottom:28px; }
        .lifetime-cta:hover { background:#92400E; border-color:#92400E; }
        .lifetime-features { max-width:320px; margin:0 auto; text-align:left; }

        /* LOADING / ERROR */
        .pricing-loading { display:flex; align-items:center; justify-content:center; gap:12px; padding:48px 0; color:var(--slate); font-size:15px; }
        .pricing-spinner { width:24px; height:24px; border:2.5px solid var(--line); border-top-color:var(--primary); border-radius:50%; animation:spin .7s linear infinite; flex-shrink:0; }
        @keyframes spin { to { transform:rotate(360deg); } }
        .pricing-error { background:#FEF3C7; border:1px solid #FCD34D; border-radius:10px; padding:16px 20px; text-align:center; font-size:14px; color:#92400E; margin-bottom:24px; }

        /* STORIES */
        .story-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:22px; }
        .story-card { border:1px solid var(--line); border-radius:12px; padding:26px 22px; background:#fff; display:flex; flex-direction:column; gap:16px; }
        .story-metric { display:inline-flex; align-items:center; gap:6px; width:fit-content; font-size:12.5px; font-weight:700; color:var(--success); background:rgba(31,138,95,.09); padding:5px 10px; border-radius:99px; }
        .story-quote { font-size:15px; line-height:1.55; color:var(--ink); }
        .story-attrib { border-top:1px solid var(--line); padding-top:14px; }
        .story-company { font-size:14px; font-weight:700; }
        .story-role { font-size:12.5px; color:var(--slate); margin-top:2px; }

        /* SECURITY */
        .security { display:flex; justify-content:center; flex-wrap:wrap; gap:36px; padding:36px 0; border-top:1px solid var(--line); border-bottom:1px solid var(--line); }
        .security-item { display:flex; align-items:center; gap:9px; font-size:14px; font-weight:600; color:var(--slate); }
        .security-item svg { color:var(--primary); }

        /* FAQ */
        .faq-list { max-width:720px; margin:0 auto; }
        .faq-item { border-bottom:1px solid var(--line); }
        .faq-q { width:100%; text-align:left; background:none; border:none; cursor:pointer; padding:18px 4px; display:flex; align-items:center; justify-content:space-between; font-family:inherit; font-size:15.5px; font-weight:600; color:var(--ink); }
        .faq-chev { transition:transform .2s; color:var(--slate); flex-shrink:0; }
        .faq-chev.open { transform:rotate(180deg); }
        .faq-a { font-size:14.5px; padding:0 4px 20px; max-width:600px; }

        /* FINAL CTA */
        .cta-band { margin:64px 0 80px; background:var(--ink); border-radius:18px; padding:56px 24px; text-align:center; }
        .cta-band h2 { color:#fff; font-size:28px; margin-bottom:10px; }
        .cta-band p { color:#AEB4BF; margin-bottom:26px; }

        button:focus-visible,.toggle-btn:focus-visible,.faq-q:focus-visible { outline:2px solid var(--primary); outline-offset:2px; }

        @media (max-width:900px) {
          .hero { padding:40px 0 48px; }
          .hero-inner { grid-template-columns:1fr; padding:0 24px; gap:36px; }
          .hero h1 { font-size:30px; }
          .hero-photo-card { left:16px; bottom:-16px; padding:12px 16px; }
          .hero-photo-card-value { font-size:20px; }
          .logobar-strip { flex-direction:column; }
          .logobar-item { border-right:none; border-bottom:1px solid var(--line); justify-content:flex-start; }
          .logobar-item:last-child { border-bottom:none; }
          .feature-grid,.pricing-grid,.story-grid { grid-template-columns:1fr; }
          .stats { grid-template-columns:repeat(2,1fr); }
          .section-head h2 { font-size:25px; }
          .free-banner { flex-direction:column; align-items:flex-start; }
          .lifetime-card { padding:28px 20px; }
          .lifetime-amount { font-size:40px; }
        }
      `}</style>
    </div>
  );
};

export default AC24100;